import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import { buildApiUrl, useBaseUrl } from '../../../utils/utils';
import localforage from 'localforage';

interface Partner {
    id: number;
    name: string;
    type: string;
    location: string;
    specialties: string[];
    referrals_in: number;
    referrals_out: number;
    last_activity: string | null;
}

interface DiscoverClinic {
    id: number;
    name: string;
    type: string;
    location: string;
    specialties: string[];
}

const PartnerNetwork: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [clinicId, setClinicId] = useState<number | null>(null);

    const [activeTab, setActiveTab] = useState<'network' | 'discover' | 'analytics'>('network');
    const [searchQuery, setSearchQuery] = useState('');
    const [discoverQuery, setDiscoverQuery] = useState('');

    const [partners, setPartners] = useState<Partner[]>([]);
    const [discoverResults, setDiscoverResults] = useState<DiscoverClinic[]>([]);
    const [loading, setLoading] = useState(true);
    const [discovering, setDiscovering] = useState(false);

    useEffect(() => {
        document.title = "Partner Network - Uzima Healthcare";
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
    }, []);

    useEffect(() => {
        if (!clinicId) return;
        setLoading(true);
        fetch(buildApiUrl(baseUrl, `facilities/partner-network?clinic_id=${clinicId}`))
            .then(res => res.json())
            .then(data => { if (data.status === 'success') setPartners(data.data || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [clinicId, baseUrl]);

    const runDiscover = async (query: string) => {
        if (!clinicId) return;
        setDiscovering(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/clinics/discover?clinic_id=${clinicId}&search=${encodeURIComponent(query)}`));
            const data = await res.json();
            if (data.status === 'success') setDiscoverResults(data.data || []);
        } catch { /* leave results as-is on failure */ } finally {
            setDiscovering(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'discover' && clinicId) runDiscover(discoverQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, clinicId]);

    const filteredPartners = partners.filter(p =>
        !searchQuery.trim() ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase())) ||
        p.location.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalReferrals = partners.reduce((s, p) => s + p.referrals_in + p.referrals_out, 0);

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">Partner Network</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/dashboard/facility">Facility</a></li>
                                            <li className="breadcrumb-item active">Partner Network</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            {[
                                { label: 'Network Partners', value: String(partners.length), icon: 'ri-links-line', color: 'success' },
                                { label: 'Total Referrals Exchanged', value: String(totalReferrals), icon: 'ri-share-forward-line', color: 'primary' },
                                { label: 'Referred In', value: String(partners.reduce((s, p) => s + p.referrals_in, 0)), icon: 'ri-download-2-line', color: 'info' },
                                { label: 'Referred Out', value: String(partners.reduce((s, p) => s + p.referrals_out, 0)), icon: 'ri-upload-2-line', color: 'warning' },
                            ].map((stat, i) => (
                                <div className="col-xl-3 col-md-6" key={i}>
                                    <div className="card card-animate">
                                        <div className="card-body">
                                            <p className="text-uppercase fw-medium text-muted mb-0">{stat.label}</p>
                                            <div className="d-flex align-items-end justify-content-between mt-4">
                                                <h4 className="fs-22 fw-semibold mb-0">{stat.value}</h4>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className={`avatar-title bg-${stat.color}-subtle rounded fs-3`}>
                                                        <i className={`${stat.icon} text-${stat.color}`}></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <ul className="nav nav-tabs-custom card-header-tabs border-bottom-0">
                                    {[
                                        { key: 'network', label: 'My Network', icon: 'ri-global-line' },
                                        { key: 'discover', label: 'Discover Partners', icon: 'ri-search-line' },
                                        { key: 'analytics', label: 'Network Analytics', icon: 'ri-bar-chart-line' },
                                    ].map(tab => (
                                        <li className="nav-item" key={tab.key}>
                                            <a className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                                               href="#" onClick={(e) => { e.preventDefault(); setActiveTab(tab.key as any); }}>
                                                <i className={`${tab.icon} me-1`}></i> {tab.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                                <button className="btn btn-primary btn-sm" onClick={() => navigate('/institution/facility/referrals')}>
                                    <i className="ri-send-plane-line me-1"></i>Start a Referral
                                </button>
                            </div>
                            <div className="card-body">
                                {activeTab === 'network' && (
                                    <>
                                        <div className="mb-4">
                                            <input type="text" className="form-control" placeholder="Search partners by name, specialty, or location..."
                                                   value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                        </div>
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                            </div>
                                        ) : filteredPartners.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ri-links-line" style={{ fontSize: '2.5rem' }}></i>
                                                <h5 className="mt-3">No network partners yet</h5>
                                                <p className="text-muted">
                                                    Your partner network is built from real referral activity - once you send or
                                                    receive a referral with another facility, they'll appear here.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="row">
                                                {filteredPartners.map(partner => (
                                                    <div className="col-lg-6 col-xl-4" key={partner.id}>
                                                        <div className="card border shadow-none">
                                                            <div className="card-body">
                                                                <div className="mb-2">
                                                                    <h6 className="mb-0">{partner.name}</h6>
                                                                    <small className="text-muted">{partner.type}{partner.location ? ` · ${partner.location}` : ''}</small>
                                                                </div>
                                                                <div className="d-flex flex-wrap gap-1 mb-3">
                                                                    {partner.specialties.map((s, i) => (
                                                                        <span key={i} className="badge bg-light text-body border">{s}</span>
                                                                    ))}
                                                                </div>
                                                                <div className="row text-center border-top pt-2">
                                                                    <div className="col-4">
                                                                        <small className="text-muted d-block">Referred In</small>
                                                                        <span className="fw-medium text-success">{partner.referrals_in}</span>
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <small className="text-muted d-block">Referred Out</small>
                                                                        <span className="fw-medium text-primary">{partner.referrals_out}</span>
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <small className="text-muted d-block">Last Activity</small>
                                                                        <span className="fw-medium">{partner.last_activity ? new Date(partner.last_activity).toLocaleDateString() : '—'}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'discover' && (
                                    <>
                                        <div className="mb-4">
                                            <div className="input-group">
                                                <input type="text" className="form-control" placeholder="Search facilities by name, specialty, or town..."
                                                       value={discoverQuery}
                                                       onChange={(e) => setDiscoverQuery(e.target.value)}
                                                       onKeyDown={(e) => { if (e.key === 'Enter') runDiscover(discoverQuery); }} />
                                                <button className="btn btn-primary" onClick={() => runDiscover(discoverQuery)}><i className="ri-search-line"></i></button>
                                            </div>
                                        </div>
                                        {discovering ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                            </div>
                                        ) : discoverResults.length === 0 ? (
                                            <div className="text-center py-5 text-muted">No facilities found.</div>
                                        ) : (
                                            <div className="row">
                                                {discoverResults.map(c => (
                                                    <div className="col-lg-6 col-xl-4" key={c.id}>
                                                        <div className="card border shadow-none">
                                                            <div className="card-body">
                                                                <h6 className="mb-0">{c.name}</h6>
                                                                <small className="text-muted d-block mb-2">{c.type}{c.location ? ` · ${c.location}` : ''}</small>
                                                                <div className="d-flex flex-wrap gap-1 mb-3">
                                                                    {c.specialties.map((s, i) => <span key={i} className="badge bg-light text-body border">{s}</span>)}
                                                                </div>
                                                                <button className="btn btn-sm btn-soft-primary w-100" onClick={() => navigate('/institution/facility/referrals')}>
                                                                    <i className="ri-send-plane-line me-1"></i>Refer a Patient
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'analytics' && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="card border shadow-none">
                                                <div className="card-header"><h6 className="mb-0">Top Partners by Volume</h6></div>
                                                <div className="card-body">
                                                    {partners.length === 0 ? (
                                                        <p className="text-muted mb-0">No referral activity yet.</p>
                                                    ) : (
                                                        partners.slice(0, 5).map(p => (
                                                            <div className="d-flex justify-content-between align-items-center py-2 border-bottom" key={p.id}>
                                                                <span>{p.name}</span>
                                                                <strong>{p.referrals_in + p.referrals_out}</strong>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="card border shadow-none">
                                                <div className="card-header"><h6 className="mb-0">Referral Balance</h6></div>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between py-2 border-bottom">
                                                        <span>Total Referred In</span>
                                                        <strong className="text-success">{partners.reduce((s, p) => s + p.referrals_in, 0)}</strong>
                                                    </div>
                                                    <div className="d-flex justify-content-between py-2 border-bottom">
                                                        <span>Total Referred Out</span>
                                                        <strong className="text-primary">{partners.reduce((s, p) => s + p.referrals_out, 0)}</strong>
                                                    </div>
                                                    <div className="d-flex justify-content-between py-2">
                                                        <span>Active Partners</span>
                                                        <strong>{partners.length}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </div>
        </>
    );
};

export default PartnerNetwork;
